import React, { useEffect, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Phone,
  PhoneOff,
  RefreshCw,
  X,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguage } from '../../../util/languages';
import './CallLogs.scss';
import { LoadingSpinner } from '../../../components/loading-spinner/LoadingSpinner';

// Type definitions
interface CallLog {
  id: string;
  reportedAt: string;
  type: 'call' | 'sms';
  phoneNumber: string;
  scamType: string;
  description?: string;
}

interface CallLogsResponse {
  data: CallLog[];
  total: number;
}

// Dummy data generator
const generateDummyData = (count: number, type: 'call' | 'sms' = 'call'): CallLog[] => {
  const scamTypes = ['Spam', 'Suspect Scam', 'Scam', 'Fraud', 'Phishing', 'Telemarketing'];
  const descriptions = [
    'Pretended to be from bank asking for OTP',
    'Offered fake investment opportunity',
    'Claimed to be from tech support',
    'Prize winning scam',
    'IRS tax collection scam',
    'Romance scam',
    'Delivery scam',
    'Loan scam',
    '',
    undefined
  ];

  const phoneNumbers = [
    '+1 (555) 123-4567',
    '+1 (555) 987-6543',
    '+1 (555) 234-5678',
    '+44 7700 900123',
    '+61 412 345 678',
    '+91 98765 43210',
    '+81 90-1234-5678',
    '+65 9123 4567'
  ];

  const data: CallLog[] = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() - Math.floor(Math.random() * 30));
    date.setHours(Math.floor(Math.random() * 24));
    date.setMinutes(Math.floor(Math.random() * 60));

    data.push({
      id: `log-${Date.now()}-${i}`,
      reportedAt: date.toISOString(),
      type: type,
      phoneNumber: phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)],
      scamType: scamTypes[Math.floor(Math.random() * scamTypes.length)],
      description: descriptions[Math.floor(Math.random() * descriptions.length)]
    });
  }

  return data;
};
type Props = {
  darkMode: any;
};

const CallLogs = (props: Props) => {
  const { t, i18n } = useTranslation();
  // Filters
  const [selectedCheckbox, setSelectedCheckbox] = useState<'call' | 'sms'>('call');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Data
  const [logs, setLogs] = useState<CallLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);

  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [noData, setNoData] = useState<boolean>(false);

  const handleCheckbox = (type: 'call' | 'sms') => {
    setSelectedCheckbox(type);
    setOffset(0); // reset pagination when type changes
  };

  const handleClearDateTime = () => {
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setNoData(false);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate dummy data based on filters
      const totalItems = 47; // Fixed total for pagination demo
      const filteredData = generateDummyData(Math.min(limit, totalItems - offset), selectedCheckbox);

      // Simulate date filtering
      let filteredLogs = filteredData;

      if (startDate) {
        const start = new Date(startDate);
        filteredLogs = filteredLogs.filter(log => new Date(log.reportedAt) >= start);
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        filteredLogs = filteredLogs.filter(log => new Date(log.reportedAt) <= end);
      }

      setLogs(filteredLogs);
      setTotal(totalItems);

      if (filteredLogs.length === 0) {
        setNoData(true);
      }

    } catch (error) {
      console.error('Error fetching scam logs:', error);
      setLogs([]);
      setNoData(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [selectedCheckbox, startDate, endDate, limit, offset]);

  // Pagination functions
  const nextPage = () => {
    if (offset + limit < total) {
      setOffset(prev => prev + limit);
    }
  };

  const prevPage = () => {
    if (offset > 0) {
      setOffset(prev => Math.max(prev - limit, 0));
    }
  };

  const getPageNumbers = (): (number | string)[] => {
    const totalPages = Math.ceil(total / limit);
    const currentPage = Math.floor(offset / limit) + 1;
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to max visible
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show pages with ellipsis logic
      if (currentPage <= 3) {
        // Show first 4 pages and last page
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show first page and last 4 pages
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Show pages around current page
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const paginate = (pageNumber: number | string) => {
    if (typeof pageNumber === 'number') {
      setOffset((pageNumber - 1) * limit);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(getLanguage(i18n.language).locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Maps a reported risk type to a design-system chip tone.
  const getRiskTone = (riskType: string) => {
    switch (riskType?.toLowerCase()) {
      case 'spam':
      case 'suspect scam':
        return 'warn';
      case 'scam':
      case 'fraud':
        return 'danger';
      case 'phishing':
        return 'violet';
      default:
        return '';
    }
  };

  // Known risk types / log types are translated at render; unknown server values are shown as-is.
  const riskTypeKeys: Record<string, string> = {
    spam: 'spam',
    'suspect scam': 'suspectScam',
    scam: 'scam',
    fraud: 'fraud',
    phishing: 'phishing',
    telemarketing: 'telemarketing',
  };
  const getRiskLabel = (riskType: string) => {
    const key = riskTypeKeys[riskType?.toLowerCase()];
    return key ? t(`cpeDetails.callLogs.riskTypes.${key}`) : riskType;
  };
  const getLogTypeLabel = (type: string) =>
    type === 'call' || type === 'sms' ? t(`cpeDetails.callLogs.logType.${type}`) : type;

  const getTypeIcon = (type: string) => {
    return type === 'call' ? (
      <Phone size={14} strokeWidth={1.75} aria-hidden />
    ) : (
      <MessageSquare size={14} strokeWidth={1.75} aria-hidden />
    );
  };

  const pageNumbers = getPageNumbers();
  const currentPage = Math.floor(offset / limit) + 1;
  // Native date picker icon follows the page theme.
  const dateScheme = { colorScheme: props.darkMode ? 'dark' : 'light' } as React.CSSProperties;

  return (
    <section className="cl-logs" id="call-logs">
      {/* Section header */}
      <header className="cl-hd">
        <div className="cl-hd-text">
          <div className="cl-hd-title">
            <h2 className="bd-h2">{t('cpeDetails.callLogs.title')}</h2>
            <span className="bd-chip">
              <Trans i18nKey="cpeDetails.callLogs.records" count={total} components={{ num: <span className="bd-mono" /> }} />
            </span>
          </div>
          <p className="bd-body cl-hd-desc">
            {t('cpeDetails.callLogs.subtitle')}
          </p>
        </div>
      </header>

      {/* Filter toolbar */}
      <div className="cl-toolbar">
        <div className="cl-toolbar-group">
          <div className="cl-field">
            <span className="bd-field-label">{t('cpeDetails.callLogs.type')}</span>
            <div className="bd-segmented" role="group" aria-label={t('cpeDetails.callLogs.logTypeAria')}>
              {[
                { id: 'call', value: 'call' as const, label: t('cpeDetails.callLogs.calls') },
                { id: 'sms', value: 'sms' as const, label: t('cpeDetails.callLogs.sms') },
              ].map(({ id, value, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleCheckbox(value)}
                  className={selectedCheckbox === value ? 'active' : ''}
                  aria-pressed={selectedCheckbox === value}
                >
                  {getTypeIcon(value)}
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="cl-field">
            <span className="bd-field-label">{t('cpeDetails.callLogs.dateRange')}</span>
            <div className="cl-dates">
              <label className="bd-search cl-date">
                <CalendarDays size={14} strokeWidth={1.75} aria-hidden />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label={t('cpeDetails.callLogs.startDate')}
                  style={dateScheme}
                />
              </label>
              <span className="cl-dates-sep" aria-hidden>
                –
              </span>
              <label className="bd-search cl-date">
                <CalendarDays size={14} strokeWidth={1.75} aria-hidden />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label={t('cpeDetails.callLogs.endDate')}
                  style={dateScheme}
                />
              </label>
              <button
                type="button"
                onClick={handleClearDateTime}
                className="bd-iconbtn cl-iconbtn"
                title={t('cpeDetails.callLogs.clearDates')}
                aria-label={t('cpeDetails.callLogs.clearDates')}
              >
                <X size={15} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        <div className="cl-field">
          <span className="bd-field-label">{t('cpeDetails.callLogs.show')}</span>
          <div className="bd-segmented" role="group" aria-label={t('cpeDetails.callLogs.rowsPerPage')}>
            {[5, 10, 20, 50].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => {
                  setLimit(num);
                  setOffset(0);
                }}
                className={`bd-mono ${limit === num ? 'active' : ''}`}
                aria-pressed={limit === num}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results summary */}
      {!loading && logs.length > 0 && (
        <div className="cl-summary">
          <div className="cl-summary-text">
            <p className="cl-summary-main">
              <Trans
                i18nKey="cpeDetails.callLogs.showing"
                count={total}
                values={{ from: offset + 1, to: Math.min(offset + limit, total) }}
                components={{ num: <span className="bd-mono" /> }}
              />
            </p>
            <p className="bd-meta">
              {t(
                startDate && endDate
                  ? 'cpeDetails.callLogs.filteredByRange'
                  : startDate
                    ? 'cpeDetails.callLogs.filteredByFrom'
                    : endDate
                      ? 'cpeDetails.callLogs.filteredByTo'
                      : 'cpeDetails.callLogs.filteredBy',
                {
                  type: selectedCheckbox === 'call' ? t('cpeDetails.callLogs.phoneCalls') : t('cpeDetails.callLogs.textMessages'),
                  start: startDate ? new Date(startDate).toLocaleDateString(getLanguage(i18n.language).locale) : '',
                  end: endDate ? new Date(endDate).toLocaleDateString(getLanguage(i18n.language).locale) : '',
                }
              )}
            </p>
          </div>

          <button type="button" onClick={fetchLogs} className="bd-pill ghost cl-refresh">
            <RefreshCw size={14} strokeWidth={1.75} aria-hidden />
            {t('cpeDetails.callLogs.refresh')}
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && <LoadingSpinner text={t('cpeDetails.callLogs.loading')} />}

      {/* Logs Table */}
      {!loading && (
        <div className="cl-table-card">
          <div className="cl-table-scroll">
            <table className="cl-table">
              <thead>
                <tr>
                  <th>{t('cpeDetails.callLogs.columns.dateTime')}</th>
                  <th>{t('cpeDetails.callLogs.columns.type')}</th>
                  <th>{t('cpeDetails.callLogs.columns.phoneNumber')}</th>
                  <th>{t('cpeDetails.callLogs.columns.riskType')}</th>
                  <th>{t('cpeDetails.callLogs.columns.description')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td className="bd-mono cl-nowrap cl-muted">{formatDate(log.reportedAt)}</td>
                      <td className="cl-nowrap">
                        <span className={`cl-type ${log.type}`}>
                          <span className="cl-type-icon">{getTypeIcon(log.type)}</span>
                          <span className="cl-type-label">{getLogTypeLabel(log.type)}</span>
                        </span>
                      </td>
                      <td className="bd-mono cl-nowrap cl-number">{log.phoneNumber}</td>
                      <td className="cl-nowrap">
                        <span className={`bd-chip ${getRiskTone(log.scamType)}`}>
                          <span className="dot" />
                          {getRiskLabel(log.scamType)}
                        </span>
                      </td>
                      <td className="cl-desc">
                        {log.description || (
                          <span className="cl-desc-empty">{t('cpeDetails.callLogs.noDescription')}</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="cl-empty-row">
                    <td colSpan={5}>
                      {noData ? (
                        <div className="cl-empty">
                          <span className="cl-empty-icon">
                            <PhoneOff size={20} strokeWidth={1.75} aria-hidden />
                          </span>
                          <h3 className="bd-h3">{t('cpeDetails.callLogs.noLogsTitle')}</h3>
                          <p className="bd-body">
                            {t('cpeDetails.callLogs.noLogsMessage')}
                          </p>
                        </div>
                      ) : (
                        <div className="cl-empty bd-meta">{t('cpeDetails.callLogs.noData')}</div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <nav className="cl-pager" aria-label={t('cpeDetails.callLogs.pagination')}>
              <button
                type="button"
                onClick={prevPage}
                disabled={offset <= 0}
                className="bd-pill ghost cl-pager-step"
              >
                <ChevronLeft size={14} strokeWidth={2} aria-hidden />
                {t('cpeDetails.callLogs.previous')}
              </button>

              <div className="cl-pager-pages">
                {pageNumbers.map((number, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => paginate(number)}
                    className={`cl-page bd-mono ${
                      currentPage === number ? 'active' : typeof number !== 'number' ? 'gap' : ''
                    }`}
                    disabled={typeof number !== 'number'}
                    aria-current={currentPage === number ? 'page' : undefined}
                    aria-label={typeof number === 'number' ? t('cpeDetails.callLogs.page', { page: number }) : undefined}
                  >
                    {number}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={nextPage}
                disabled={offset + limit >= total}
                className="bd-pill ghost cl-pager-step"
              >
                {t('cpeDetails.callLogs.next')}
                <ChevronRight size={14} strokeWidth={2} aria-hidden />
              </button>
            </nav>
          )}
        </div>
      )}
    </section>
  );
};

export default CallLogs;
